import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { cn } from '../lib/utils';

/**
 * Figma Design System Button
 * https://www.figma.com/design/XAyZZhRbJ3DewYR0ITFfed/Whif?node-id=16522-71755
 *
 * Variants: default | white(Accent) | destructive | outline | ghost | link | gray
 *   └ white = Figma/문서 명칭 'Accent' (하위호환 위해 코드 prop 값은 'white' 유지)
 * Sizes: sm (h-8/32px) | default (h-9/36px) | lg (h-10/40px) | xlg (h-12/48px) | icon (36x36)
 *
 * Width: isFullWidth={true} for 100%, or className="w-[120px]" for fixed width
 */

const shadowXs = 'shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]';

const whifButtonVariants = cva(
  `
    inline-flex items-center justify-center whitespace-nowrap rounded-full
    transition-colors cursor-pointer
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-outline-brand-primary-focused
    focus-visible:ring-offset-2
    disabled:pointer-events-none disabled:opacity-40
    [&_svg]:pointer-events-none [&_svg]:shrink-0
  `,
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        sm: 'h-8 gap-global-spacing-6 px-global-spacing-12 text-label-xs-sb',
        default:
          'h-9 gap-global-spacing-8 px-global-spacing-16 text-label-sm-sb',
        lg: 'h-10 gap-global-spacing-8 px-global-spacing-32 text-label-sm-sb',
        xlg: 'h-12 gap-global-spacing-8 px-global-spacing-32 text-label-sm-sb',
        icon: 'size-9 gap-global-spacing-0 p-global-spacing-0',
      },
      // WDS Semantic Token(Figma SOT) 배선. 유틸은 namespace 포함(bg-bg-*/text-text-*).
      // hover/pressed = SOT 상태 오버레이(color-mix 프리믹스). destructive 라벨은
      // 정본 text/error/inverse(옅은 적색) — 흰색 아님.
      variant: {
        default: `
          bg-bg-brand-primary-solid text-text-brand-primary-inverse
          hover:bg-bg-brand-primary-solid-hover
          active:bg-bg-brand-primary-solid-pressed
          ${shadowXs}
        `,
        // Figma/문서 명칭 'Accent'. 테마 적응형 solid 버튼 —
        // bg-bg-neutral-solid: 라이트 black / 다크 cg-5. 라벨 text-neutral-inverse(역상).
        white: `
          bg-bg-neutral-solid text-text-neutral-inverse
          hover:bg-bg-neutral-solid-hover
          active:bg-bg-neutral-solid-pressed
          ${shadowXs}
        `,
        destructive: `
          bg-bg-error-solid text-text-error-inverse
          hover:bg-bg-error-solid-hover
          active:bg-bg-error-solid-pressed
          ${shadowXs}
        `,
        outline: `
          border border-line-border-neutral-subtle bg-transparent text-text-neutral-solid
          hover:bg-bg-neutral-weak-hover
          active:bg-bg-neutral-weak-pressed
          ${shadowXs}
        `,
        ghost: `
          bg-transparent text-text-neutral-solid
          hover:bg-bg-neutral-weak-hover
          active:bg-bg-neutral-weak-pressed
        `,
        link: `
          bg-transparent text-text-neutral-solid underline-offset-4
          hover:bg-bg-neutral-weak-hover hover:underline
          active:bg-bg-neutral-weak-pressed
        `,
        gray: `
          bg-bg-neutral-subtle text-text-neutral-solid
          hover:bg-bg-neutral-subtle-hover
          active:bg-bg-neutral-subtle-pressed
        `,
      },
    },
  }
);

export interface WhifButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof whifButtonVariants> {
  asChild?: boolean;
  isFullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const WhifButton = forwardRef<HTMLButtonElement, WhifButtonProps>(
  (
    {
      asChild = false,
      className,
      size,
      variant,
      isFullWidth,
      isLoading,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isIconSize = size === 'icon';

    const showLoadingSpinner = isLoading && !isIconSize && !asChild;

    const content = asChild ? (
      children
    ) : (
      <>
        {showLoadingSpinner && (
          <svg
            className='animate-spin'
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M21 12a9 9 0 1 1-6.219-8.56' />
          </svg>
        )}
        {!showLoadingSpinner && leftIcon}
        {children}
        {!showLoadingSpinner && rightIcon}
      </>
    );

    const fullWidthClassName = isFullWidth ? 'w-full' : '';
    const loadingClassName = isLoading ? 'opacity-60' : '';

    return (
      <Comp
        type={asChild ? undefined : 'button'}
        className={cn(
          whifButtonVariants({ className, size, variant }),
          fullWidthClassName,
          loadingClassName
        )}
        ref={ref}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {content}
      </Comp>
    );
  }
);
WhifButton.displayName = 'WhifButton';

export { WhifButton, whifButtonVariants };
